import type React from "react";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import apiService from "../../../service/Api";

const BADGE_DURATION_MS = 14 * 24 * 60 * 60 * 1000;

type Environment = "local" | "staging" | "production";

interface FeatureBadgeLaunchResponse {
  launched_at: string;
}

interface NewFeatureBadgeProps {
  children: React.ReactNode;
  feature: string;
  label?: string;
}

const getEnvironment = (): Environment => {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1") return "local";
  if (hostname.includes("staging")) return "staging";
  return "production";
};

const NewFeatureBadge: React.FC<NewFeatureBadgeProps> = ({
  children,
  feature,
  label = "NEW",
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    let timeout: number | undefined;

    apiService
      .get<FeatureBadgeLaunchResponse>(
        "/api/configurations/feature-badge-launch/",
        { feature, environment: getEnvironment() },
        true,
      )
      .then(({ data }) => {
        if (!active) return;
        const expiresAt = new Date(data.launched_at).getTime() + BADGE_DURATION_MS;
        const remaining = expiresAt - Date.now();
        setVisible(remaining > 0);
        if (remaining > 0) {
          timeout = window.setTimeout(() => setVisible(false), remaining);
        }
      })
      .catch(() => {
        if (active) setVisible(false);
      });

    return () => {
      active = false;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [feature]);

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {children}
      {visible && (
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            backgroundColor: "#FF3B30",
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.03em",
            borderRadius: "999px",
            px: 0.75,
            py: 0.25,
            lineHeight: 1.4,
            pointerEvents: "none",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
            zIndex: 1,
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );
};

export default NewFeatureBadge;
